import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {__} from "@wordpress/i18n";
import classNames from 'classnames';
import SearchInput from './SearchInput';
import SearchSuggestions from './SearchSuggestions';
import SearchDropdown from './SearchDropdown';
import SearchButton from './SearchButton';
import SearchClearButton from './SearchClearButton';

/**
 *
 * @param {string} id The ID.
 * @param {string} endpoint key of the endpoint of wp_variables.
 * @param {int} searchBoxIndex the searchbox index.
 * @param {string} resultsHeight the max-height of the results dropdown, default is 'auto'.
 *
 * @constructor
 */
const DynamicSearch = ({id, endpoint, searchBoxIndex = 0, resultsHeight = 'auto'}) => {
  if (!id || !endpoint) {
    return;
  }

  const {
    [endpoint]: _ENDPOINT,
    search_labels: _LABELS,
    search_url: _URL,
    search_endpoint_type: _SEARCH_TYPE,
    search_endpoint_post_types: _POST_TYPES,
    svg_sprite: _SPRITE
  } = window.wp_variables;

  if (!_ENDPOINT) {
    return;
  }

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dismissed, setDismissed] = useState(false);

  // Cache key for sessionStorage
  const getCacheKey = useCallback((term) => {
    const domain = window.location.hostname;
    const language = document.documentElement.lang || 'en';
    return `GDH[${domain}][${language}][${term}]`;
  }, []);

  // Get cached suggestions
  const getCachedSuggestions = useCallback((term) => {
    try {
      const cached = sessionStorage.getItem(getCacheKey(term));
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  }, [getCacheKey]);

  // Cache suggestions
  const cacheSuggestions = useCallback((term, data) => {
    try {
      sessionStorage.setItem(getCacheKey(term), JSON.stringify(data));
    } catch {
      // sessionStorage might be full or disabled
    }
  }, [getCacheKey]);

  // Clear cache on page navigation
  useEffect(() => {
    const clearCache = () => {
      try {
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key?.startsWith('GDH[')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
      } catch {
        // Ignore errors
      }
    };

    window.addEventListener('beforeunload', clearCache);
    return () => window.removeEventListener('beforeunload', clearCache);
  }, []);

  // Listen for variable translations and force re-render
  useEffect(() => {
    const handleVariablesTranslated = () => {
      setForceUpdate(prev => prev + 1);
    };

    document.addEventListener('acato-deepl:variables-translated', handleVariablesTranslated);

    return () => {
      document.removeEventListener('acato-deepl:variables-translated', handleVariablesTranslated);
    };
  }, []);

  // Notify DeepL when component mounts or when suggestions change
  useEffect(() => {
    if (wrapperRef.current && suggestions.length > 0) {
      const event = new CustomEvent('acato-deepl:content-updated', {
        detail: { element: wrapperRef.current }
      });
      document.dispatchEvent(event);
    }
  }, [suggestions]);

  // Recalculate panel height after this component mounts.
  useEffect( () => {
    const panelElement = wrapperRef.current?.closest( '[data-panel-depth]' );
    if (panelElement?.id) {
      window.dispatchEvent( new CustomEvent( 'panel:updateHeight', {
        detail: {refresh: true, id: panelElement.id},
        bubbles: true
      } ) );
    }
  }, [wrapperRef] );

  // Auto-focus input when the containing panel opens.
  useEffect(() => {
    const panelElement = wrapperRef.current?.closest('[data-panel-depth]');
    if (!panelElement?.id) return;

    const handlePanelOpen = (e) => {
      if (e?.detail?.id === panelElement.id) {
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    };

    window.addEventListener('panel:open', handlePanelOpen);
    return () => window.removeEventListener('panel:open', handlePanelOpen);
  }, []);

  const listBoxAriaID = `denhaag-search-results__list-${searchBoxIndex}`;

  // Reset activeIndex when suggestions change
  useEffect(() => setActiveIndex(-1), [suggestions]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0) {
      const activeElement = document.getElementById(`${listBoxAriaID}-option-${activeIndex}`);
      activeElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, listBoxAriaID]);

  const changeHandler = useCallback((event) => { setSearch(event.target.value); setDismissed(false); }, []);

  const clearInput = useCallback(() => {
    setSearch('');
    setSuggestions([]);
    setActiveIndex(-1);
  }, []);

  const closeDropdown = useCallback(() => {
    setSuggestions([]);
    setActiveIndex(-1);
    setDismissed(true);
  }, []);

  const postsPerPage = useMemo(() => window.matchMedia('(min-width:768px)').matches ? 8 : 6, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSearch('');
        setSuggestions([]);
        setActiveIndex(-1);
      }
    };

    const handleFocusOut = (event) => {
      // Safari and Firefox do not focus buttons on click, so clicking our own
      // submit/clear button blurs the input with a null relatedTarget. Bailing out
      // here prevents the search term from being wiped before the form submits
      // (DHNL-156). Genuine outside clicks are still handled by handleClickOutside.
      if (!event.relatedTarget) return;
      if (wrapperRef.current && !wrapperRef.current.contains(event.relatedTarget)) {
        setSearch('');
        setSuggestions([]);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    wrapperRef.current?.addEventListener('focusout', handleFocusOut);

    const currentWrapper = wrapperRef.current;
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      currentWrapper?.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const handleKeyDown = useCallback((event) => {
    const hasResults = suggestions.length > 0;

    switch (event.key) {
      case 'ArrowDown':
        if (hasResults) {
          event.preventDefault();
          const nextIndex = activeIndex < suggestions.length - 1 ? activeIndex + 1 : 0;
          setActiveIndex(nextIndex);
          // Focus the link at nextIndex
          const nextElement = suggestionsRef.current?.querySelector(`#${listBoxAriaID}-option-${nextIndex} a`);
          nextElement?.focus();
        }
        break;
      case 'ArrowUp':
        if (hasResults) {
          event.preventDefault();
          const prevIndex = activeIndex > 0 ? activeIndex - 1 : suggestions.length - 1;
          setActiveIndex(prevIndex);
          // Focus the link at prevIndex
          const prevElement = suggestionsRef.current?.querySelector(`#${listBoxAriaID}-option-${prevIndex} a`);
          prevElement?.focus();
        }
        break;
      case 'Home':
        if (hasResults) {
          event.preventDefault();
          setActiveIndex(0);
          const firstElement = suggestionsRef.current?.querySelector(`#${listBoxAriaID}-option-0 a`);
          firstElement?.focus();
        }
        break;
      case 'End':
        if (hasResults) {
          event.preventDefault();
          const lastIndex = suggestions.length - 1;
          setActiveIndex(lastIndex);
          const lastElement = suggestionsRef.current?.querySelector(`#${listBoxAriaID}-option-${lastIndex} a`);
          lastElement?.focus();
        }
        break;
      case 'Escape':
        // Only intercept Escape when a dropdown is visible, otherwise close the containing panel.
        if (suggestions.length > 0 || (search.length > 3 && !loading && !dismissed)) {
          event.preventDefault();
          closeDropdown();
          inputRef.current?.focus();
        } else {
          const panelElement = wrapperRef.current?.closest('[data-panel-depth]');
          if (panelElement?.id) {
            window.dispatchEvent(new CustomEvent('panel:close', {
              detail: { id: panelElement.id },
              bubbles: true
            }));
          }
        }
        break;
      case 'Enter':
        if (activeIndex >= 0 && suggestions[activeIndex]) {
          event.preventDefault();
          const item = suggestions[activeIndex];
          const url = 'relevanssi' === _SEARCH_TYPE ? item.link : item.url;
          window.location.href = url;
        }
        break;
    }
  }, [suggestions, activeIndex, closeDropdown, _SEARCH_TYPE, listBoxAriaID, search, loading, dismissed]);

  // Keyboard handler for suggestion items
  const handleSuggestionKeyDown = useCallback((event, index) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = index < suggestions.length - 1 ? index + 1 : 0;
        setActiveIndex(nextIndex);
        const nextElement = suggestionsRef.current?.querySelector(`#${listBoxAriaID}-option-${nextIndex} a`);
        nextElement?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = index > 0 ? index - 1 : suggestions.length - 1;
        setActiveIndex(prevIndex);
        const prevElement = suggestionsRef.current?.querySelector(`#${listBoxAriaID}-option-${prevIndex} a`);
        prevElement?.focus();
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        const firstElement = suggestionsRef.current?.querySelector(`#${listBoxAriaID}-option-0 a`);
        firstElement?.focus();
        break;
      case 'End':
        event.preventDefault();
        const lastIndex = suggestions.length - 1;
        setActiveIndex(lastIndex);
        const lastElement = suggestionsRef.current?.querySelector(`#${listBoxAriaID}-option-${lastIndex} a`);
        lastElement?.focus();
        break;
      case 'Escape':
        event.preventDefault();
        closeDropdown();
        inputRef.current?.focus();
        break;
    }
  }, [suggestions.length, closeDropdown, listBoxAriaID]);

  useEffect(() => {
    if (search.length < 3) {
      setSuggestions([]);
      return;
    }

    // Check cache first
    const cached = getCachedSuggestions(search);
    if (cached) {
      setSuggestions(cached);
      return;
    }

    const abortController = new AbortController();

    const doSearch = async () => {
      setLoading(true);

      let params = {};
      switch (endpoint) {
        case 'rest_api_url_autocomplete':
          params = {
            s: search,
            posts_per_page: postsPerPage
          };
          break;

        case 'rest_api_url':
        default:
          params = 'relevanssi' === _SEARCH_TYPE ? {
            s: search,
            posts_per_page: postsPerPage
          } : {
            search: search,
            per_page: postsPerPage
          };
          break;
      }

      if (!!_POST_TYPES && _POST_TYPES.length > 0) {
        params['post_type[]'] = _POST_TYPES;
      }

      try {
        // Build URL with query parameters
        const url = new URL(_ENDPOINT);
        const { searchParams } = url;

        Object.keys(params).forEach(key => {
          if (Array.isArray(params[key])) {
            params[key].forEach(value => searchParams.append(key, value));
          } else {
            searchParams.set(key, params[key]);
          }
        });

        const response = await fetch(url.toString(), { signal: abortController.signal });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const results = 'relevanssi' === _SEARCH_TYPE ? data['posts'] : data;

        // Cache the results
        cacheSuggestions(search, results);
        setSuggestions(results);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      }

      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    };

    const searchTimer = setTimeout(() => doSearch(), 500);
    return () => {
      clearTimeout(searchTimer);
      abortController.abort();
    };

  }, [search, getCachedSuggestions, cacheSuggestions, endpoint, postsPerPage, _SEARCH_TYPE, _POST_TYPES, _ENDPOINT]);

  // URL with SearchTerm.
  // @source: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURI
  const _RESULTS_URL = useMemo(() => `${_URL}${encodeURI(search).replace(/%5B/g, '[').replace(/%5D/g, ']')}`, [_URL, search]);

  const _CLASSES = useMemo(() => ({
    root: classNames('denhaag-search-form', {
      ['denhaag-search-form--open']: search && !loading
    }),
    inner: 'denhaag-search-form__inner'
  }), [search, loading]);

  // Set default field name.
  const { fieldName, search_url } = useMemo(() => {
    let name = 's';
    const url = new URL(_URL, _ENDPOINT);
    if (1 === url.searchParams.size) {
      url.searchParams.forEach((value, key) => {
        name = key;
      });
    }
    return { fieldName: name, search_url: url };
  }, [_URL, _ENDPOINT]);

  const hasResults = search && !loading && suggestions.length > 0;
  const activeDescendant = activeIndex >= 0 ? `${listBoxAriaID}-option-${activeIndex}` : undefined;

  return (
    <div ref={wrapperRef} className={_CLASSES.root} role="search">
      <form action={search_url.pathname}>
        <label htmlFor={`${id}-field`} className="denhaag-search-form__label utrecht-heading-4">{_LABELS.label}</label>
        <div className={_CLASSES.inner}>
          <div
            role="status"
            aria-live='polite'
            aria-atomic="true"
            className='sr-only'
          >
            {hasResults ?
              __(`${suggestions.length} results are available.`, 'nlds-theme')
              :
              ''
            }
          </div>
          <SearchInput
            ref={inputRef}
            id={`${id}-field`}
            value={search}
            loading={loading}
            placeholder={_LABELS.placeholder}
            onChange={changeHandler}
            onKeyDown={handleKeyDown}
            name={fieldName}
            ariaOwns={listBoxAriaID}
            expanded={hasResults}
            activeDescendant={activeDescendant}
          />
          {search.length > 0 && !loading && <SearchClearButton onClick={clearInput} />}
          <SearchButton label={_LABELS.button} />
        </div>
        {hasResults && (
          <SearchDropdown maxHeight={resultsHeight}>
            <SearchSuggestions
              ref={suggestionsRef}
              items={suggestions}
              term={search}
              ariaID={listBoxAriaID}
              searchBoxIndex={searchBoxIndex}
              activeIndex={activeIndex}
              onItemKeyDown={handleSuggestionKeyDown}
            />
          </SearchDropdown>
        )}
        {search.length > 3 && !loading && !dismissed && 0 === suggestions.length && (
          <SearchDropdown>
            <ul className="utrecht-link-list utrecht-link-list--html-ul denhaag-link-group__list">
                <li className="denhaag-link-group__list-item">
                  <a className="denhaag-link" href={_RESULTS_URL} target="_self">
                      <svg className="denhaag-icon" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <use href={`${_SPRITE}#nlds-icon-enter`}/>
                      </svg>
                    { _LABELS.for } <strong>{ search }</strong>
                  </a>
                </li>

            </ul>
          </SearchDropdown>
        )}
      </form>
    </div>
  );
};

export default DynamicSearch;
